import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, Copy, Eye, Lock } from 'lucide-react';
import { problemsApi } from '@/api/endpoints';
import { getApiErrorMessage } from '@/api/client';
import type { OfficialSolutionResponse } from '@/api/types';
import { useAuthStore } from '@/store/auth';

const DIALECT_LABEL: Record<string, string> = {
  POSTGRESQL: 'PostgreSQL',
  MYSQL: 'MySQL',
  SQLITE: 'SQLite',
  MSSQL: 'SQL Server',
  ORACLE: 'Oracle',
};

/**
 * "Yechimlar" — rasmiy yechim kodi. Tab ochilganda yuklanadi (prerender HTML ga tushmaydi);
 * kod tasodifan ko'rinib qolmasligi uchun avval "Yechimni ko'rsatish" tugmasi.
 */
export function SolutionsTab({ slug }: { slug: string }) {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const [data, setData] = useState<OfficialSolutionResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  const viewerKey = `${user?.id ?? ''}|${user?.isPremium ?? ''}`;

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    problemsApi
      .solution(slug)
      .then((res) => !cancelled && setData(res.data))
      .catch((err) => !cancelled && setError(getApiErrorMessage(err, t('problem.solutionsError'))));
    return () => {
      cancelled = true;
    };
  }, [slug, viewerKey, t]);

  // Boshqa masalaga o'tilsa yana yashiramiz
  useEffect(() => setRevealed(false), [slug]);

  if (error) return <div className="py-12 text-center text-content-secondary">{error}</div>;
  if (!data) return <div className="py-12 text-center text-content-secondary">{t('common.loading')}</div>;

  if (data.locked) {
    return (
      <div className="rounded-lg border border-[var(--medium)]/30 bg-[var(--medium)]/10 p-6 text-center">
        <Lock className="mx-auto mb-3 h-10 w-10 text-[var(--medium)]" />
        <h2 className="mb-2 text-lg font-semibold">{t('problem.solutionsLockedTitle')}</h2>
        <p className="mb-4 text-sm text-content-secondary">{t('problem.solutionsLocked')}</p>
        <Link
          to={isAuthenticated ? '/pricing' : '/login'}
          className="btn btn-primary inline-flex gap-2 px-5 py-2.5 text-sm font-semibold"
        >
          {isAuthenticated ? t('problem.premiumCta') : t('problem.editorialLoginCta')}
        </Link>
      </div>
    );
  }

  if (data.solutions.length === 0) {
    return <div className="py-12 text-center text-content-secondary">{t('problem.solutionsEmpty')}</div>;
  }

  if (!revealed) {
    return (
      <div className="rounded-lg border border-line p-6 text-center">
        <p className="mb-4 text-sm text-content-secondary">{t('problem.solutionsRevealHint')}</p>
        <button onClick={() => setRevealed(true)} className="btn btn-secondary inline-flex gap-2 px-4 py-2 text-sm font-semibold">
          <Eye className="h-4 w-4" /> {t('problem.solutionsReveal')}
        </button>
      </div>
    );
  }

  const copy = async (dialect: string, sql: string) => {
    try {
      await navigator.clipboard.writeText(sql);
      setCopied(dialect);
      window.setTimeout(() => setCopied((c) => (c === dialect ? null : c)), 1500);
    } catch {
      /* clipboard ruxsat berilmagan — jim */
    }
  };

  return (
    <div className="space-y-4">
      {data.solutions.map((s) => (
        <div key={s.dialect}>
          <div className="flex items-center justify-between rounded-t-lg border border-line bg-brand-500/5 px-3 py-1.5 text-xs font-semibold">
            <span className="font-mono">{DIALECT_LABEL[s.dialect] ?? s.dialect}</span>
            <button
              onClick={() => copy(s.dialect, s.sql)}
              className="inline-flex items-center gap-1 text-content-secondary transition-colors hover:text-content"
            >
              {copied === s.dialect ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              {copied === s.dialect ? t('problem.solutionsCopied') : t('problem.solutionsCopy')}
            </button>
          </div>
          <pre className="overflow-x-auto rounded-b-lg border border-t-0 border-line p-3">
            <code className="font-mono text-[0.85em] leading-relaxed text-content-secondary">{s.sql}</code>
          </pre>
        </div>
      ))}
    </div>
  );
}
